// Usage events, batched and sent with sendBeacon to /api/event.
// No cookies and no account. The only identifier is a random id kept in this
// browser so return visits can be counted. An event is a name, a time and a
// few short fields; nothing typed by the player is ever included.
export const EVENTS = new Set(['session', 'draw', 'open', 'first_touch', 'play_time', 'share', 'install']);
export const KEY = 'mofu-metrics-v1';
const BATCH = 20, DELAY = 5000;

export class Metrics {
  constructor({ storage = null, send = null, now = Date.now, enabled = true, context = {}, random = Math.random } = {}) {
    this.storage = storage; this.send = send; this.now = now; this.enabled = enabled; this.context = context; this.random = random;
    this.queue = []; this.timer = null;
    this.id = this.loadId();
  }
  loadId() {
    try {
      const data = JSON.parse(this.storage?.getItem(KEY));
      if (typeof data?.id === 'string' && /^[a-z0-9]{8,32}$/.test(data.id)) return data.id;
    } catch { /* A missing or corrupt id is replaced below. */ }
    const id = Array.from({ length: 16 }, () => Math.floor(this.random() * 36).toString(36)).join('');
    try { this.storage?.setItem(KEY, JSON.stringify({ id })); } catch { /* Session-only id. */ }
    return id;
  }
  track(name, data = {}) {
    if (!this.enabled || !EVENTS.has(name)) return false;
    const fields = {};
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') fields[key] = value.slice(0, 40);
      else if (typeof value === 'number' && Number.isFinite(value)) fields[key] = value;
      else if (typeof value === 'boolean') fields[key] = value ? 1 : 0;
    }
    this.queue.push({ name, at: this.now(), ...this.context, ...fields });
    if (this.queue.length >= BATCH) this.flush();
    else if (!this.timer) { this.timer = setTimeout(() => this.flush(), DELAY); this.timer.unref?.(); }
    return true;
  }
  flush() {
    clearTimeout(this.timer); this.timer = null;
    if (!this.queue.length || !this.send) return false;
    const events = this.queue.splice(0);
    try { return this.send(JSON.stringify({ id: this.id, events })) !== false; }
    catch { return false; }
  }
}

// sendBeacon survives page unload; fetch with keepalive is the fallback.
export function beaconSender(url = '/api/event') {
  return body => {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    }
    if (typeof fetch === 'function') {
      fetch(url, { method: 'POST', body, headers: { 'content-type': 'application/json' }, keepalive: true }).catch(() => {});
      return true;
    }
    return false;
  };
}

// Seconds spent on a toy with the page visible, reported when play ends.
export class PlayTimer {
  constructor(metrics, now = () => performance.now()) { this.metrics = metrics; this.now = now; this.start = null; this.kind = null; }
  update(playing, kind) {
    if (playing && this.start !== null && kind === this.kind) return;
    if (this.start !== null) {
      const seconds = Math.round((this.now() - this.start) / 1000);
      if (seconds >= 1) this.metrics.track('play_time', { kind: this.kind, value: seconds });
      this.start = null; this.kind = null;
    }
    if (playing) { this.start = this.now(); this.kind = kind; }
  }
}
